import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SectionHead from '@/components/SectionHead';
import { GALLERY, GALLERY_CATEGORIES } from '@/data/content';
import { fadeUp, viewportOnce } from '@/lib/motion';

export default function Gallery() {
  const [active, setActive] = useState('All');
  const items = active === 'All' ? GALLERY : GALLERY.filter(g => g.category === active);

  return (
    <section id="gallery" className="relative py-24 sm:py-32 bg-night" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <SectionHead
          chapter="13"
          eyebrow="Gallery"
          title={'INSIDE THE\nACADEMY.'}
          testId="gallery-heading"
        />

        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce}
          className="flex flex-wrap gap-2.5 mb-10"
          data-testid="gallery-filters"
        >
          {GALLERY_CATEGORIES.map(c => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`gallery-filter-${c.toLowerCase()}`}
              className={`font-display tracking-widest text-base sm:text-lg uppercase px-4 py-1.5 rounded-full border transition-colors duration-300 ${
                active === c ? 'bg-brand-gold text-night border-brand-gold' : 'border-white/15 text-gray-400 hover:border-brand-gold/50 hover:text-brand-gold'
              }`}
            >
              {c}
            </button>
          ))}
        </motion.div>

        <motion.div layout className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="gallery-grid">
          <AnimatePresence mode="popLayout">
            {items.map((g, i) => (
              <motion.figure
                layout
                key={g.src}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.45, delay: i * 0.04 }}
                className={`group relative overflow-hidden rounded-sm border border-white/10 hover:border-brand-gold/60 transition-[border-color] duration-300 ${i % 5 === 0 ? 'col-span-2 row-span-2' : ''}`}
                data-testid={`gallery-item-${g.category.toLowerCase()}-${i}`}
              >
                <img src={g.src} alt={g.alt} loading="lazy"
                  className="w-full h-full min-h-[160px] object-cover aspect-[4/3] group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-80" />
                <figcaption className="absolute bottom-3 left-3 text-[10px] font-bold uppercase tracking-[0.25em] text-brand-gold bg-black/60 backdrop-blur-sm px-2.5 py-1 rounded-sm">
                  {g.category}
                </figcaption>
              </motion.figure>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
